import { useQuery } from "@tanstack/react-query";
import { apiClient, assertJsonArray } from "src/lib/apiClient";
import { keys, staleTimes } from "src/queries/keys";

/**
 * Countries open for merchant registration, from GET /merchant/available-countries
 * — the same CountryPricing table the backend registration gate reads. While
 * loading (or if the fetch fails) `countries` is null and callers should not
 * block: the backend gate remains the authority.
 */
export interface AvailableCountry {
  country: string;
  country_code: string;
  is_free: boolean;
}

export const useAvailableCountries = () => {
  const { data, isPending, isError } = useQuery({
    queryKey: keys.reference.availableCountries(),
    staleTime: staleTimes.reference,
    queryFn: async () => {
      const response = await apiClient.get("merchant/available-countries");
      // A misconfigured API base URL turns this into a same-origin request
      // answered by the SPA fallback (index.html, 200) — accept only a real
      // array so the form falls back to the backend gate.
      return assertJsonArray<AvailableCountry>(
        response.data,
        "/merchant/available-countries"
      );
    },
  });

  // `null` on both load and failure is load-bearing: the form must not block
  // registration when this is unavailable.
  return { countries: data ?? null, loading: isPending && !isError };
};
